import {useEffect, useRef, type Ref} from "react";
import type {PdfPosition} from "../contentUtils";
import type {PdfTocEntryDto} from "../types";
import classes from "./PdfTableOfContents.module.css";

interface Props {
    outline: PdfTocEntryDto[];
    onNavigate: (entry: PdfTocEntryDto) => void;
    /** The URL's position (a search hit or a TOC click): the entry it falls under is marked active. */
    position?: PdfPosition | null;
}

// Entries have no stable id in every source (bookmarks: null) — key on position.
const entryKey = (entry: PdfTocEntryDto) =>
    `${entry.id ?? entry.title ?? ""}|${entry.pageIndex}|${entry.y ?? "page"}`;

// y is bottom-origin, so higher on the page is larger. No y, or y 0, is the
// page top (the bookmark convention, see PdfViewer's goToBookmark).
const heightOnPage = (y: number | null) => (y === null || y <= 0 ? Infinity : y);

/** The last entry, in document order, at or above the position. */
const entryAt = (outline: PdfTocEntryDto[], position: PdfPosition): PdfTocEntryDto | null => {
    let found: PdfTocEntryDto | null = null;
    const visit = (entries: PdfTocEntryDto[]) => {
        for (const entry of entries) {
            const atOrAbove =
                entry.pageIndex < position.pageIndex ||
                (entry.pageIndex === position.pageIndex &&
                    heightOnPage(entry.y) >= heightOnPage(position.y));
            if (!atOrAbove) return;
            found = entry;
            visit(entry.children);
        }
    };
    visit(outline);
    return found;
};

const TocList = ({
    entries,
    activeKey,
    onSelect,
    nested = false,
    listRef,
}: {
    entries: PdfTocEntryDto[];
    activeKey: string | null;
    onSelect: (entry: PdfTocEntryDto) => void;
    nested?: boolean;
    listRef?: Ref<HTMLUListElement>;
}) => (
    <ul ref={listRef} className={nested ? classes.children : classes.list}>
        {entries.map((entry) => {
            const key = entryKey(entry);
            const isActive = key === activeKey;
            return (
                <li key={key}>
                    <button
                        type="button"
                        className={`${classes.link}${isActive ? ` ${classes.active}` : ""}`}
                        aria-current={isActive ? "location" : undefined}
                        title={entry.title ?? undefined}
                        onClick={() => onSelect(entry)}
                    >
                        {entry.title || entry.numbering || "—"}
                    </button>
                    {entry.children.length > 0 && (
                        <TocList
                            entries={entry.children}
                            activeKey={activeKey}
                            onSelect={onSelect}
                            nested
                        />
                    )}
                </li>
            );
        })}
    </ul>
);

/**
 * Admin's table of contents for a PDF: a nested list of buttons, every level
 * expanded (no collapse in the POC); the active entry is the one the URL's
 * position falls under, which a TOC click sets too.
 * Navigation itself belongs to the caller (the viewer handle's goToBookmark);
 * the <nav> landmark is the layout's TOC column, like the HTML page's.
 */
export const PdfTableOfContents = ({outline, onNavigate, position}: Props) => {
    const active = position ? entryAt(outline, position) : null;
    const activeKey = active ? entryKey(active) : null;
    const listRef = useRef<HTMLUListElement>(null);

    // Keep the marked entry in view in a long TOC, as the HTML page's TOC
    // does (useTocScrollSync): only when it is outside the TOC column, so a
    // click on a visible entry never moves the list.
    useEffect(() => {
        const button = listRef.current?.querySelector('[aria-current="location"]');
        const column = listRef.current?.closest("nav");
        if (!button || !column) return;
        const columnRect = column.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        if (buttonRect.top >= columnRect.top && buttonRect.bottom <= columnRect.bottom) return;
        button.scrollIntoView({block: "center"});
    }, [activeKey]);

    if (outline.length === 0) return null;
    return (
        <TocList
            entries={outline}
            activeKey={activeKey}
            onSelect={onNavigate}
            listRef={listRef}
        />
    );
};
